import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useApp } from "../../context/AppContext";
import api from "../../lib/api";
import adminApi from "../../lib/adminApi";
import { ADMIN } from "../../lib/adminI18n";
import { ORIENT_SECTIONS, sectionStoreName } from "../../lib/catalog";
import { Loader2, Download, CheckCircle2 } from "lucide-react";

export default function ImportTab() {
  const { lang } = useApp();
  const a = ADMIN[lang];
  const [seed, setSeed] = useState([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, created: 0, skipped: 0, failed: 0 });
  const [log, setLog] = useState([]);

  useEffect(() => { fetch("/orient_products.json").then((r) => r.json()).then(setSeed).catch(() => {}); }, []);

  const addLog = (m) => setLog((l) => [m, ...l].slice(0, 20));

  const run = async () => {
    if (!seed.length) return;
    setRunning(true);
    setProgress({ done: 0, total: seed.length, created: 0, skipped: 0, failed: 0 });
    setLog([]);
    let created = 0, skipped = 0, failed = 0, done = 0;
    try {
      const storesData = await api.getStores();
      const custom = storesData?.custom || [];
      const allProducts = (await api.getStoreProducts())?.products || [];

      for (const sec of ORIENT_SECTIONS) {
        const items = seed.filter((p) => p.section === sec.slug);
        if (!items.length) continue;
        const storeName = sectionStoreName(sec.sv);
        let store = custom.find((s) => s.name === storeName);
        if (!store) {
          try {
            const res = await adminApi.createStore({ name: storeName, category: "matbutik", color: sec.color });
            store = res?.store || res;
            addLog(`✓ ${a.ensureStore}: ${sec.sv}`);
          } catch (e) { addLog(`✗ store ${sec.sv}: ${e?.response?.data?.detail || e.message}`); }
        }
        const storeId = store?.store_id;
        const existing = new Set(allProducts.filter((p) => p.store_id === storeId).map((p) => p.name));

        for (const it of items) {
          done++;
          if (!storeId) { failed++; setProgress({ done, total: seed.length, created, skipped, failed }); continue; }
          if (existing.has(it.name)) { skipped++; setProgress({ done, total: seed.length, created, skipped, failed }); continue; }
          try {
            await adminApi.createProduct({ store_id: storeId, name: it.name, price: it.price, old_price: null, emoji: it.emoji, image_path: null, is_custom: true });
            created++;
          } catch (e) { failed++; }
          if (done % 25 === 0) { setProgress({ done, total: seed.length, created, skipped, failed }); addLog(`${sec.sv}: ${done}/${seed.length}`); }
        }
        setProgress({ done, total: seed.length, created, skipped, failed });
      }
      toast.success(`${a.importDone} · ${created} ${a.created}`);
      addLog(`✅ ${a.importDone}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || a.error);
    } finally {
      setRunning(false);
    }
  };

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div data-testid="admin-import" className="max-w-2xl">
      <div className="rounded-2xl border border-border bg-card p-6">
        <h3 className="font-head font-bold text-lg mb-1 flex items-center gap-2"><Download className="h-5 w-5 text-primary" /> {a.importTitle}</h3>
        <p className="text-sm text-muted-foreground mb-4">{a.importDesc} · {seed.length || "…"} {a.products?.toLowerCase?.() || ""}</p>

        {running || progress.total > 0 ? (
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1"><span>{progress.done}/{progress.total}</span><span>{pct}%</span></div>
            <div className="h-2.5 rounded-full bg-accent overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>
            <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
              <span className="text-emerald-600">{a.created}: {progress.created}</span>
              <span>{a.skipped}: {progress.skipped}</span>
              <span className="text-red-600">{a.failed}: {progress.failed}</span>
            </div>
          </div>
        ) : null}

        <button data-testid="admin-import-start" disabled={running || !seed.length} onClick={run}
          className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
          {running ? <><Loader2 className="h-5 w-5 animate-spin" /> {a.importing}</> : <><Download className="h-5 w-5" /> {a.startImport}</>}
        </button>

        {!running && progress.done > 0 && progress.done >= progress.total && (
          <div className="mt-3 flex items-center gap-2 text-emerald-600 text-sm font-semibold"><CheckCircle2 className="h-4 w-4" /> {a.importDone}</div>
        )}

        {log.length > 0 && (
          <div className="mt-4 rounded-xl bg-background border border-border p-3 max-h-48 overflow-y-auto no-scrollbar text-xs font-mono space-y-1">
            {log.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        )}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">{a.importNote}</p>
    </div>
  );
}
